// Table/Drawer.tsx
import { useState, useMemo, useEffect, useRef, useLayoutEffect } from "react";
import { ScrollView, StyleSheet, View } from "react-native";
import { Drawer } from "@/components/Drawer";
import { Button } from "@/components/ui/Button";
import { Collapsible } from "@/components/Table/Collapsible";
import ContextMenu from "@/components/ContextMenu";
import Search from "@/components/Search";
import { useTheme } from "@/hooks/useTheme";
import {
  FieldSpec,
  Query,
  MenuType,
  getDisplayName,
  LinkedRecordConfig,
  makeDefaultQuery,
} from "./types";
import { FilterMenu, VisibleMenu, OrderByMenu, GroupByMenu } from "./Menus";
import { ThemedText } from "../ThemedText";
import ThemedInput from "@/components/ThemedInput";
import { useStorage } from "@/hooks/useStorage";
import { profileService } from "@/services/profileServices";
import useAuth from "@/hooks/useAuth";
import { useToolTip } from "@/hooks/useToolTip";

type TableDrawerProps<FN extends string = string> = {
  filterChoices: {
    name: FN;
    kind: "text" | "number" | "boolean" | "date" | "decimal" | "enum" | "array" | "linked_record" | "iso_week";
    menus?: MenuType[];
    displayName?: string | ((name?: string) => string);
    options?: { value: string; label?: string; color?: string }[];
    linkedRecord?: LinkedRecordConfig;
  }[];
  page: string;
  onQueryChange?: (query: Query<FN>) => void;
  externalQuery?: Query<FN>;
};

export function TableDrawer<FN extends string>({
  filterChoices,
  page,
  onQueryChange,
  externalQuery,
}: TableDrawerProps<FN>) {
  const { colors } = useTheme();
  const { user } = useAuth();
  const storage = useStorage();
  const toolTip = useToolTip();

  // Refs for button measurements
  const bookmarkRef = useRef<View>(null);
  const collapseRef = useRef<View>(null);
  const filterRef = useRef<View>(null);
  const visibleRef = useRef<View>(null);
  const orderByRef = useRef<View>(null);
  const groupByRef = useRef<View>(null);
  const resetRef = useRef<View>(null);
  const saveRef = useRef<View>(null);

  const savedCollapsed = storage.getBoolean("table-drawer") ?? true;

  const menuHelper = [
    { key: "filter", state: false },
    { key: "visible", state: false },
    { key: "orderby", state: false },
    { key: "groupby", state: false },
  ];

  // State
  const [menuOpen, setMenuOpen] = useState(false);
  const [savedQueries, setSavedQueries] = useState<Query<FN>[]>([]);
  const [isCollapsed, setIsCollapsed] = useState(savedCollapsed);
  const [searchValue, setSearchValue] = useState("");
  const [collapsedMenuState, setCollapsedMenuState] = useState<{ key: string; state: boolean }[]>(menuHelper);
  const [subMenuState, setSubMenuState] = useState<{ key: string; state: boolean }[]>(menuHelper);

  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveQueryName, setSaveQueryName] = useState("");

  // Layout measurements for tooltips
  const [bookmarkLayout, setBookmarkLayout] = useState({ pageX: 0, pageY: 0, width: 0, height: 0 });
  const [collapseLayout, setCollapseLayout] = useState({ pageX: 0, pageY: 0, width: 0, height: 0 });
  const [filterLayout, setFilterLayout] = useState({ pageX: 0, pageY: 0, width: 0, height: 0 });
  const [visibleLayout, setVisibleLayout] = useState({ pageX: 0, pageY: 0, width: 0, height: 0 });
  const [orderByLayout, setOrderByLayout] = useState({ pageX: 0, pageY: 0, width: 0, height: 0 });
  const [groupByLayout, setGroupByLayout] = useState({ pageX: 0, pageY: 0, width: 0, height: 0 });
  const [resetLayout, setResetLayout] = useState({ pageX: 0, pageY: 0, width: 0, height: 0 });
  const [saveLayout, setSaveLayout] = useState({ pageX: 0, pageY: 0, width: 0, height: 0 });

  // Initialize with empty query if none provided
  const defaultQuery = makeDefaultQuery<FN>();

  // Query state - single source of truth, changes immediately affect table
  const [query, setQuery] = useState(defaultQuery);
  const [loadedQuery, setLoadedQuery] = useState<Query<FN> | null>(null);
  const [selectedQueryId, setSelectedQueryId] = useState<string | null>(null);

  // Sync internal query state when external query changes (e.g. from header cell sort)
  useEffect(() => {
    if (!externalQuery) return;
    setQuery((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(externalQuery)) return prev;
      return externalQuery;
    });
  }, [externalQuery]);

  // Can reset if: (loaded query exists and differs) OR (no loaded query and differs from default)
  const canReset =
    loadedQuery !== null
      ? JSON.stringify(query) !== JSON.stringify(loadedQuery)
      : JSON.stringify(query) !== JSON.stringify(defaultQuery);

  // Helper to update query and notify parent immediately
  const updateQuery = (partial: Partial<Query<FN>>) => {
    setQuery((prev) => {
      const next = { ...prev, ...partial };
      onQueryChange?.(next);
      return next;
    });
  };

  // Load last used query from storage on mount
  const loadLastQuery = async () => {
    const lastQueryId = storage.getString(`${page}-lastqueryid`);
    if (!lastQueryId || !user?.id) return;

    const queries = await profileService.getQueriesByPage<FN>(user.id, page);
    const lastQuery = queries.find((q) => q.id === lastQueryId);

    if (lastQuery) {
      setQuery(lastQuery);
      setLoadedQuery(lastQuery);
      setSelectedQueryId(lastQuery.id || null);
      onQueryChange?.(lastQuery);
    }
  };

  // Load last query on mount
  useEffect(() => {
    if (user?.id) {
      loadLastQuery();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, page]);

  // Measure button positions for tooltips
  useLayoutEffect(() => {
    bookmarkRef.current?.measure((_x, _y, width, height, pageX, pageY) => {
      setBookmarkLayout({ pageX, pageY, width, height });
    });
    collapseRef.current?.measure((_x, _y, width, height, pageX, pageY) => {
      setCollapseLayout({ pageX, pageY, width, height });
    });
    filterRef.current?.measure((_x, _y, width, height, pageX, pageY) => {
      setFilterLayout({ pageX, pageY, width, height });
    });
    visibleRef.current?.measure((_x, _y, width, height, pageX, pageY) => {
      setVisibleLayout({ pageX, pageY, width, height });
    });
    orderByRef.current?.measure((_x, _y, width, height, pageX, pageY) => {
      setOrderByLayout({ pageX, pageY, width, height });
    });
    groupByRef.current?.measure((_x, _y, width, height, pageX, pageY) => {
      setGroupByLayout({ pageX, pageY, width, height });
    });
    resetRef.current?.measure((_x, _y, width, height, pageX, pageY) => {
      setResetLayout({ pageX, pageY, width, height });
    });
    saveRef.current?.measure((_x, _y, width, height, pageX, pageY) => {
      setSaveLayout({ pageX, pageY, width, height });
    });
  }, [isCollapsed, canReset]);

  const drawerStyle = useMemo(
    () => [styles.drawer, isCollapsed ? styles.drawerCollapsed : styles.drawerExpanded, { borderColor: colors.border }],
    [isCollapsed, colors.border],
  );

  const toggleCollapse = () =>
    setIsCollapsed((p) => {
      storage.saveFast("table-drawer", !p);
      return !p;
    });

  const handleMenu = (p: boolean = false, text: string) => {
    setSubMenuState((prev) => {
      return [...prev.filter((s) => s.key !== text), { key: text, state: p }];
    });
  };

  const handleFind = (text: string) => {
    if (isCollapsed) {
      return collapsedMenuState?.find((s) => s?.key === text)?.state ?? false;
    }
    return subMenuState?.find((s) => s?.key === text)?.state ?? false;
  };

  const handleCollapsedButton = (text: string) => {
    setCollapsedMenuState((prev) => {
      return [...prev.filter((s) => s.key !== text), { key: text, state: !prev.find((r) => r.key === text)?.state }];
    });
  };

  const kindMap = {
    number: "integer",
    decimal: "decimal",
    boolean: "boolean",
    text: "text",
    date: "date",
    enum: "enum",
    array: "array",
    linked_record: "linked_record",
    iso_week: "iso_week",
  } as const;

  const catalog: readonly FieldSpec<FN>[] = filterChoices
    ? filterChoices.map((c) => ({
        name: c.name as FN,
        kind: kindMap[c.kind],
        menus: c.menus,
        displayName: c.displayName,
        options: c.options,
        linkedRecord: c.linkedRecord,
      }))
    : [];

  // Filter choices by menu type - if menus not specified, show in all menus
  const filterForMenu = (menuType: MenuType) =>
    catalog.filter((c) => !c.menus || c.menus.length === 0 || c.menus.includes(menuType));

  // Reset to the loaded saved query
  const resetToLoaded = () => {
    if (loadedQuery) {
      setQuery(loadedQuery);
      onQueryChange?.(loadedQuery);
    }
  };

  // Load a saved query
  const loadSavedQuery = (savedQuery: Query<FN>) => {
    setQuery(savedQuery);
    setLoadedQuery(savedQuery);
    onQueryChange?.(savedQuery);
    setSelectedQueryId(savedQuery.id || null);
    setMenuOpen(false);

    // Save query ID to storage for persistence
    if (savedQuery.id) {
      storage.saveFast(`${page}-lastqueryid`, savedQuery.id);
    }
  };

  // Start a new unsaved query
  const startNewQuery = () => {
    setQuery(defaultQuery);
    setLoadedQuery(null);
    setSelectedQueryId(null);
    onQueryChange?.(defaultQuery);
    setMenuOpen(false);
    storage.removeItem(`${page}-lastqueryid`);
  };

  // Save current query (overwrites if same name exists)
  const saveCurrentQuery = async () => {
    if (!user?.id || !saveQueryName.trim()) return;

    // If query with same name exists, delete it first (overwrite by name)
    const existingQuery = savedQueries.find((q) => q.name === saveQueryName);
    if (existingQuery?.id) {
      await profileService.deleteSavedQueryByPage(user.id, page, existingQuery.id);
    }

    const queryToSave = {
      ...query,
      name: saveQueryName,
      id: Date.now().toString(),
    };

    await profileService.saveQueryByPage(user.id, page, queryToSave);
    await fetchQueries();
    setSelectedQueryId(queryToSave.id);
    setLoadedQuery(queryToSave); // This is now our loaded query
    setShowSaveDialog(false);
    setSaveQueryName("");
  };

  // Delete a query
  const deleteQuery = async (queryId: string) => {
    if (!user?.id) return;
    await profileService.deleteSavedQueryByPage(user.id, page, queryId);
    await fetchQueries();
    if (selectedQueryId === queryId) {
      setSelectedQueryId(null);
    }
  };

  // Fetch saved queries
  const fetchQueries = async () => {
    if (!user?.id) return;
    const data = await profileService.getQueriesByPage<FN>(user.id, page);
    setSavedQueries(data.sort((a, b) => (a.name || "").localeCompare(b.name || "")));
  };

  // Filtered saved queries based on search
  const filteredQueries = useMemo(() => {
    if (!searchValue) return savedQueries;
    return savedQueries.filter((q) => q.name?.toLowerCase().includes(searchValue.toLowerCase()));
  }, [savedQueries, searchValue]);

  // Count badges
  // Count fields with at least one expression
  const filterCount = query.filters?.filter((f) => f.expressions?.length > 0).length || 0;
  const hiddenCount = query.visible?.filter((v) => v.visible === false).length || 0;
  const orderByCount = query.orderby ? 1 : 0;
  const groupByCount = query.groupby?.length ?? 0;

  // Check if saving will overwrite an existing query
  const willOverwrite = savedQueries.some((q) => q.name === saveQueryName.trim());

  useEffect(() => {
    fetchQueries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, user?.id]);

  const filterMenu = (
    <FilterMenu<FN>
      initial={query.filters}
      menuToggle={(p) => handleMenu(p, "filter")}
      choices={filterForMenu("filter")}
      onChange={(filters) => updateQuery({ filters })}
      filterMatchMode={query.filterMatchMode}
      onFilterMatchModeChange={(filterMatchMode) => updateQuery({ filterMatchMode })}
    />
  );

  const visibleMenu = (
    <VisibleMenu<FN>
      initial={query.visible}
      menuToggle={(p) => handleMenu(p, "visible")}
      choices={filterForMenu("visible")}
      onChange={(visible) => updateQuery({ visible })}
    />
  );

  const orderbyMenu = (
    <OrderByMenu
      initial={query.orderby}
      menuToggle={(p) => handleMenu(p, "orderby")}
      choices={filterForMenu("orderby").map((c) => ({ name: c.name.toString(), displayName: getDisplayName(c) }))}
      onChange={(orderby) => updateQuery({ orderby })}
    />
  );

  const groupbyMenu = (
    <GroupByMenu
      initial={query.groupby}
      menuToggle={(p) => handleMenu(p, "groupby")}
      choices={filterForMenu("groupby").map((c) => ({ name: c.name.toString(), displayName: getDisplayName(c) }))}
      onChange={(groupby) => updateQuery({ groupby })}
    />
  );

  return (
    <Drawer style={drawerStyle}>
      {/* Header / Saved queries */}
      <View style={isCollapsed ? styles.headerRowCollapsed : styles.headerRow}>
        <Button
          iconPosition="right"
          ref={bookmarkRef}
          text={
            !isCollapsed
              ? selectedQueryId
                ? savedQueries.find((q) => q.id === selectedQueryId)?.name
                : "Unsaved Search"
              : undefined
          }
          textStyle={!isCollapsed && !selectedQueryId ? { opacity: 0.6 } : undefined}
          icon={isCollapsed ? "bookmark" : "chevron-down"}
          style={[styles.headerBtn, !isCollapsed && styles.headerBtnExpanded]}
          onPress={() => {
            toolTip.setVisible(false);
            setMenuOpen((prev) => !prev);
          }}
          onHoverIn={() => {
            if (isCollapsed) {
              toolTip.setToolTipContent(
                <View style={[styles.tooltip, { backgroundColor: colors.background, borderColor: colors.border }]}>
                  <ThemedText>Saved Searches</ThemedText>
                </View>,
              );
              toolTip.setLocation({ x: bookmarkLayout.pageX + bookmarkLayout.width + 4, y: bookmarkLayout.pageY + 2 });
              toolTip.setVisible(true);
            }
          }}
          onHoverOut={() => {
            toolTip.setVisible(false);
          }}
        />

        <Button
          ref={collapseRef}
          style={styles.collapseButton}
          icon={isCollapsed ? "arrow-expand-right" : "arrow-collapse-left"}
          onPress={() => {
            toolTip.setVisible(false);
            toggleCollapse();
          }}
          accessibilityLabel={isCollapsed ? "Expand drawer" : "Collapse drawer"}
          onHoverIn={() => {
            toolTip.setToolTipContent(
              <View style={[styles.tooltip, { backgroundColor: colors.background, borderColor: colors.border }]}>
                <ThemedText>{isCollapsed ? "Expand" : "Collapse"}</ThemedText>
              </View>,
            );
            toolTip.setLocation({ x: collapseLayout.pageX + collapseLayout.width + 4, y: collapseLayout.pageY + 2 });
            toolTip.setVisible(true);
          }}
          onHoverOut={() => {
            toolTip.setVisible(false);
          }}
        />
      </View>

      <ContextMenu
        visible={menuOpen}
        setVisible={setMenuOpen}
        ignoreRef={bookmarkRef}
        style={[styles.menu, isCollapsed && { top: 58, left: 58 }]}
      >
        <View>
          <Search id={`search-${page}-querries`} autoFocus value={searchValue} setValue={setSearchValue} />
        </View>
        {filteredQueries.length > 0 ? (
          filteredQueries.map((q) => (
            <View key={q.id} style={styles.queryItem}>
              <Button
                style={[styles.queryButton, selectedQueryId === q.id && { backgroundColor: colors.backgroundHover }]}
                text={q.name || "Unnamed"}
                onPress={() => loadSavedQuery(q)}
              />
              <Button icon="trash-can-outline" style={styles.deleteButton} onPress={() => deleteQuery(q.id!)} />
            </View>
          ))
        ) : (
          <ThemedText style={{ opacity: 0.7, alignSelf: "center", marginVertical: 8 }} type="small">
            No saved searches
          </ThemedText>
        )}
        <Button
          icon="plus"
          text="New Search"
          style={styles.newSearchButton}
          textStyle={{ fontSize: 12, color: colors.textSecondary }}
          iconColor={colors.textSecondary}
          onPress={startNewQuery}
        />
      </ContextMenu>

      <ContextMenu
        visible={showSaveDialog}
        setVisible={setShowSaveDialog}
        ignoreRef={saveRef}
        style={[styles.saveDialog, isCollapsed && { top: 154, left: 58 }]}
      >
        <View style={styles.saveDialogContent}>
          <ThemedText type="subtitle">Save Search</ThemedText>
          <ThemedInput
            autoFocus
            placeholder="Enter query name"
            value={saveQueryName}
            onChangeText={setSaveQueryName}
            style={{ marginVertical: 8 }}
          />
          <View style={styles.saveDialogButtons}>
            <Button
              text="Cancel"
              style={styles.cancelButton}
              textStyle={{ color: colors.text }}
              onPress={() => {
                setShowSaveDialog(false);
                setSaveQueryName("");
              }}
            />
            <Button
              text={willOverwrite ? "Overwrite!" : "Save"}
              style={[styles.confirmButton, { backgroundColor: colors.primary }]}
              textStyle={{ color: "white" }}
              onPress={saveCurrentQuery}
              disabled={!saveQueryName.trim()}
            />
          </View>
        </View>
      </ContextMenu>

      {/* Categories */}
      {!isCollapsed ? (
        <>
          <View style={[styles.categories, menuOpen && { zIndex: 1 }]}>
            {/* Action buttons */}
            <View style={[styles.actionButtons, { borderColor: colors.border }]}>
              <Button
                text="Reset"
                style={[styles.resetButton, !canReset && { opacity: 0.5 }]}
                textStyle={{ color: colors.text }}
                onPress={canReset ? (loadedQuery ? resetToLoaded : startNewQuery) : undefined}
                hoverColor={canReset ? undefined : "transparent"}
              />
              <Button
                text="Save Search"
                style={[styles.saveButton, { borderColor: colors.primary }]}
                textStyle={{ color: colors.primary }}
                onPress={() => {
                  setSaveQueryName(query.name || "");
                  setShowSaveDialog(true);
                }}
              />
            </View>
            <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
              <Collapsible
                title="Filter"
                badge={filterCount > 0 ? filterCount.toString() : undefined}
                menOpen={handleFind("filter")}
              >
                {filterMenu}
              </Collapsible>
              <Collapsible
                title="Visible"
                badge={hiddenCount > 0 ? hiddenCount.toString() : undefined}
                menOpen={handleFind("visible")}
              >
                {visibleMenu}
              </Collapsible>
              <Collapsible title="Sort by" badge={orderByCount > 0 ? "1" : undefined} menOpen={handleFind("orderby")}>
                {orderbyMenu}
              </Collapsible>
              <Collapsible
                title="Group by"
                badge={groupByCount > 0 ? groupByCount.toString() : undefined}
                menOpen={handleFind("groupby")}
              >
                {groupbyMenu}
              </Collapsible>
            </ScrollView>
          </View>
        </>
      ) : (
        <View style={styles.iconRail}>
          <View style={{ gap: 8 }}>
            <Button
              ref={resetRef}
              style={[styles.resetButton, !canReset && { opacity: 0.5 }]}
              textStyle={[{ color: colors.text }, styles.railBtn]}
              icon="restart"
              onPress={canReset ? (loadedQuery ? resetToLoaded : startNewQuery) : undefined}
              hoverColor={canReset ? undefined : "transparent"}
              onHoverIn={() => {
                toolTip.setToolTipContent(
                  <View style={[styles.tooltip, { backgroundColor: colors.background, borderColor: colors.border }]}>
                    <ThemedText>Reset</ThemedText>
                  </View>,
                );
                toolTip.setLocation({ x: resetLayout.pageX + resetLayout.width + 4, y: resetLayout.pageY + 2 });
                toolTip.setVisible(true);
              }}
              onHoverOut={() => {
                toolTip.setVisible(false);
              }}
            />
            <Button
              ref={saveRef}
              style={[styles.saveButton, styles.railBtn, { borderColor: colors.primary }]}
              textStyle={[{ color: colors.primary }, styles.railBtn]}
              icon="content-save"
              iconColor={colors.primary}
              onPress={() => {
                toolTip.setVisible(false);
                setSaveQueryName(query.name || "");
                setShowSaveDialog(true);
              }}
              onHoverIn={() => {
                toolTip.setToolTipContent(
                  <View style={[styles.tooltip, { backgroundColor: colors.background, borderColor: colors.border }]}>
                    <ThemedText>Save Search</ThemedText>
                  </View>,
                );
                toolTip.setLocation({ x: saveLayout.pageX + saveLayout.width + 4, y: saveLayout.pageY + 2 });
                toolTip.setVisible(true);
              }}
              onHoverOut={() => {
                toolTip.setVisible(false);
              }}
            />
          </View>
          <View style={{ position: "relative" }}>
            <Button
              ref={filterRef}
              icon="filter"
              style={styles.railBtn}
              accessibilityLabel="Filter"
              onPress={() => {
                toolTip.setVisible(false);
                handleCollapsedButton("filter");
              }}
              onHoverIn={() => {
                toolTip.setToolTipContent(
                  <View style={[styles.tooltip, { backgroundColor: colors.background, borderColor: colors.border }]}>
                    <ThemedText>Filter</ThemedText>
                  </View>,
                );
                toolTip.setLocation({ x: filterLayout.pageX + filterLayout.width + 4, y: filterLayout.pageY + 2 });
                toolTip.setVisible(true);
              }}
              onHoverOut={() => {
                toolTip.setVisible(false);
              }}
            />
            {filterCount > 0 ? (
              <ThemedText style={[styles.collapsedBadge, { backgroundColor: colors.dangerBorder }]}>
                {filterCount}
              </ThemedText>
            ) : null}
          </View>
          <ContextMenu
            visible={handleFind("filter")}
            setVisible={() => handleCollapsedButton("filter")}
            ignoreRef={filterRef}
            style={[{ width: 360 }, isCollapsed && { top: 86, left: 52 }]}
          >
            {filterMenu}
          </ContextMenu>

          <View style={{ position: "relative" }}>
            <Button
              ref={visibleRef}
              icon="eye"
              style={styles.railBtn}
              accessibilityLabel="Visible"
              onPress={() => {
                toolTip.setVisible(false);
                handleCollapsedButton("visible");
              }}
              onHoverIn={() => {
                toolTip.setToolTipContent(
                  <View style={[styles.tooltip, { backgroundColor: colors.background, borderColor: colors.border }]}>
                    <ThemedText>Visible</ThemedText>
                  </View>,
                );
                toolTip.setLocation({ x: visibleLayout.pageX + visibleLayout.width + 4, y: visibleLayout.pageY + 2 });
                toolTip.setVisible(true);
              }}
              onHoverOut={() => {
                toolTip.setVisible(false);
              }}
            />
            {hiddenCount > 0 ? (
              <ThemedText style={[styles.collapsedBadge, { backgroundColor: colors.dangerBorder }]}>
                {hiddenCount}
              </ThemedText>
            ) : null}
          </View>
          <ContextMenu
            visible={handleFind("visible")}
            setVisible={() => handleCollapsedButton("visible")}
            ignoreRef={visibleRef}
            style={[{ width: 360 }, isCollapsed && { top: 128, left: 52 }]}
          >
            {visibleMenu}
          </ContextMenu>

          <View style={{ position: "relative" }}>
            <Button
              ref={orderByRef}
              icon="sort"
              style={styles.railBtn}
              accessibilityLabel="Sort by"
              onPress={() => {
                toolTip.setVisible(false);
                handleCollapsedButton("orderby");
              }}
              onHoverIn={() => {
                toolTip.setToolTipContent(
                  <View style={[styles.tooltip, { backgroundColor: colors.background, borderColor: colors.border }]}>
                    <ThemedText>Sort by</ThemedText>
                  </View>,
                );
                toolTip.setLocation({ x: orderByLayout.pageX + orderByLayout.width + 4, y: orderByLayout.pageY + 2 });
                toolTip.setVisible(true);
              }}
              onHoverOut={() => {
                toolTip.setVisible(false);
              }}
            />
            {orderByCount > 0 ? (
              <ThemedText style={[styles.collapsedBadge, { backgroundColor: colors.dangerBorder }]}>1</ThemedText>
            ) : null}
          </View>
          <ContextMenu
            visible={handleFind("orderby")}
            setVisible={() => handleCollapsedButton("orderby")}
            ignoreRef={orderByRef}
            style={[{ width: 360 }, isCollapsed && { top: 170, left: 52 }]}
          >
            {orderbyMenu}
          </ContextMenu>

          <View style={{ position: "relative" }}>
            <Button
              ref={groupByRef}
              icon="layers"
              style={styles.railBtn}
              accessibilityLabel="Group by"
              onPress={() => {
                toolTip.setVisible(false);
                handleCollapsedButton("groupby");
              }}
              onHoverIn={() => {
                toolTip.setToolTipContent(
                  <View style={[styles.tooltip, { backgroundColor: colors.background, borderColor: colors.border }]}>
                    <ThemedText>Group by</ThemedText>
                  </View>,
                );
                toolTip.setLocation({ x: groupByLayout.pageX + groupByLayout.width + 4, y: groupByLayout.pageY + 2 });
                toolTip.setVisible(true);
              }}
              onHoverOut={() => {
                toolTip.setVisible(false);
              }}
            />
            {groupByCount > 0 ? (
              <ThemedText style={[styles.collapsedBadge, { backgroundColor: colors.dangerBorder }]}>
                {groupByCount}
              </ThemedText>
            ) : null}
          </View>
          <ContextMenu
            visible={handleFind("groupby")}
            setVisible={() => handleCollapsedButton("groupby")}
            ignoreRef={groupByRef}
            style={[{ width: 360 }, isCollapsed && { top: 212, left: 52 }]}
          >
            {groupbyMenu}
          </ContextMenu>
        </View>
      )}
    </Drawer>
  );
}

const styles = StyleSheet.create({
  drawer: {
    gap: 8,
    padding: 8,
    borderRightWidth: StyleSheet.hairlineWidth,
    flexDirection: "column",
    position: "absolute",
  },
  drawerExpanded: {
    boxShadow: "8px 0 12px 0 rgba(0, 0, 0, 0.25)",
    elevation: 8,
  },
  drawerCollapsed: {
    width: 56,
    paddingHorizontal: 6,
  },
  headerRowCollapsed: {
    flexDirection: "column-reverse",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    zIndex: 1,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    zIndex: 1,
  },
  headerBtn: {
    height: 36,
    minWidth: 36,
    justifyContent: "center",
    paddingHorizontal: 8,
    flexGrow: 1,
  },
  headerBtnExpanded: {
    justifyContent: "space-between",
  },
  collapseButton: {
    width: 36,
    height: 36,
  },
  menu: {
    top: 48,
    zIndex: 2,
    width: 360,
    maxHeight: 400,
  },
  queryItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
  },
  queryButton: {
    flexGrow: 1,
    justifyContent: "flex-start",
    backgroundColor: "transparent",
    borderColor: "transparent",
  },
  deleteButton: {
    width: 32,
    height: 32,
    backgroundColor: "transparent",
    borderColor: "transparent",
  },
  newQueryButton: {
    alignItems: "center",

    backgroundColor: "transparent",
    borderWidth: StyleSheet.hairlineWidth,
    borderStyle: "dashed",
    borderRadius: 8,
    gap: 8,
    paddingTop: 8,
  },
  categories: {
    gap: 6,
    zIndex: 2,
    flex: 1,
  },
  actionButtons: {
    flexDirection: "row",
    gap: 8,
    paddingBottom: 8,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  resetButton: {
    flex: 1,
    minHeight: 36,
    maxHeight: 36,
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#e0e0e0",
  },
  applyButton: {
    flex: 1,
    minHeight: 36,
    maxHeight: 36,
  },
  saveButton: {
    flex: 1,
    minHeight: 36,
    maxHeight: 36,
    backgroundColor: "transparent",
    borderWidth: 1,
  },
  iconRail: {
    gap: 6,
    alignItems: "center",
    paddingVertical: 4,
  },
  railBtn: {
    width: 36,
    height: 36,
  },
  saveDialog: {
    zIndex: 3,
    width: 360,
    padding: 16,
  },
  saveDialogContent: {
    gap: 8,
  },
  saveDialogButtons: {
    flexDirection: "row",
    gap: 8,
    marginTop: 8,
  },
  cancelButton: {
    flex: 1,
    height: 36,
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#e0e0e0",
  },
  confirmButton: {
    flex: 1,
    height: 36,
  },
  newSearchButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: "#ccc",
    marginTop: 4,
    gap: 4,
  },
  collapsedBadge: {
    top: 0,
    position: "absolute",
    left: 30,
    minWidth: 18,
    height: 18,
    paddingHorizontal: 4,
    borderRadius: 9,
    color: "white",
    fontSize: 12,
    fontWeight: "600",
    textAlign: "center",
    lineHeight: 18,
  },
  tooltip: {
    borderWidth: 1,
    borderRadius: 10,
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
});
